import { Constants, useMeeting, useParticipant, usePubSub } from "../../config/sdkConfig";

import React, { useMemo } from "react";
import MicOffIcon from "../../icons/ParticipantTabPanel/MicOffIcon";
import MicOnIcon from "../../icons/ParticipantTabPanel/MicOnIcon";
import RaiseHand from "../../icons/ParticipantTabPanel/RaiseHand";
import VideoCamOffIcon from "../../icons/ParticipantTabPanel/VideoCamOffIcon";
import VideoCamOnIcon from "../../icons/ParticipantTabPanel/VideoCamOnIcon";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { nameTructed } from "../../utils/helper";
import { useSiteContext } from "../../context/SiteContext";

const COLOR_PALETTE = [
  "#00b894", // Teal
  "#7700FF", // Purple
  "#FF6B6B", // Red
  "#4ECDC4", // Turquoise
  "#45B7D1", // Blue
  "#FFA07A", // Light Salmon
  "#98D8C8", // Mint
  "#F7DC6F", // Yellow
  "#BB8FCE", // Lavender
  "#85C1E2", // Sky Blue
  "#F8B739", // Orange
  "#52BE80", // Green
  "#EB984E", // Peach
  "#AF7AC5", // Plum
  "#76D7C4", // Aqua
  "#F1C40F", // Gold
  "#E74C3C", // Dark Red
  "#3498DB", // Royal Blue
  "#16A085", // Dark Teal
  "#D35400", // Dark Orange
];

// Function to generate a consistent color from a string using custom palette
function generateColorFromName(name) {
  if (!name) return COLOR_PALETTE[0]; // Default teal color

  // Hash the string to get a consistent number
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Select a color from the palette based on hash
  const index = Math.abs(hash % COLOR_PALETTE.length);
  return COLOR_PALETTE[index];
}

function WebinarParticipantListItem({ participantId, raisedHand, isPresenting }) {
  const { micOn, webcamOn, displayName, isLocal, remove, mode, metaData } =
    useParticipant(participantId);
  const { meetingRole, meetingSettings, isCoHost } = useSiteContext();
  const { publish } = usePubSub("MUTE_MIC");
  const { publish: publishStopScreenShare } = usePubSub("STOP_SCREEN_SHARE");
  const { publish: publishParticipantRequest } = usePubSub(
    "WEBINAR_PARTICIPANT_REQUEST"
  );
  const { publish: publishParticipantModeChangeListener } = usePubSub(
    "WEBINAR_PARTICIPANT_MODE_CHANGE_LISTENER"
  );

  const isTargetHost = metaData?.meetingRole === 'moderator' || metaData?.meetingRole === 'host';
  const isTargetCoHost = isCoHost(participantId);
  const canModerate = meetingRole === "moderator" || meetingRole === "host" || (meetingSettings.allowCoHostsManage && meetingRole === "co-host");
  const isHost = meetingRole === "moderator" || meetingRole === "host";

  // Determine if this participant is in CONFERENCE mode (can broadcast)
  const isPresenter = mode === Constants.modes.SEND_AND_RECV;

  // Generate unique color for each participant
  const avatarColor = generateColorFromName(displayName);

  return (
    <div className="mt-2 m-2 p-2 bg-gray-700 rounded-lg mb-0">
      <div className="flex flex-1 items-center justify-center relative">
        <div
          style={{
            color: "#ffffff",
            backgroundColor: avatarColor,
          }}
          className="h-10 w-10 text-lg mt-0 rounded overflow-hidden flex relative items-center justify-center"
        >
          {displayName?.charAt(0).toUpperCase()}
        </div>
        <div className="ml-2 mr-1 flex flex-1 flex-col">
          <p className="text-base text-white overflow-hidden whitespace-pre-wrap overflow-ellipsis">
            {isLocal ? "You" : nameTructed(displayName, 15)}
          </p>
        </div>
        {raisedHand && (
          <div className="flex items-center justify-center m-1 p-1 pointer-events-none">
            <RaiseHand fillcolor={"#fff"} />
          </div>
        )}
        {/* Only show mic controls for presenters or if moderator controlling them */}
        {isPresenter && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              console.log("Webinar participant panel - role:", meetingRole, "micOn:", micOn, "participantId:", participantId);
              if (meetingRole === "moderator" && micOn) {
                console.log("Publishing MUTE_MIC for participant:", participantId);
                publish(
                  {
                    participantId: participantId,
                  },
                  {
                    persist: false,
                  }
                );
              }
            }}
            className={`m-1 p-1 ${micOn && meetingRole === "moderator" ? "cursor-pointer" : ""
              }`}
          >
            {micOn ? <MicOnIcon /> : <MicOffIcon />}
          </div>
        )}
        {isPresenter && (
          <div className="m-1 p-1">
            {webcamOn ? <VideoCamOnIcon /> : <VideoCamOffIcon />}
          </div>
        )}

        {/* Stop Screen Share - Only for host/co-host, only when presenting, not for host/co-host targets */}
        {isPresenting && canModerate && !isLocal && !isTargetHost && (isHost || !isTargetCoHost) && (
          <div
            className="m-1 p-1 cursor-pointer hover:bg-orange-500/20 rounded transition-colors"
            onClick={() => {
              console.log(`Stopping screen share for webinar participant: ${displayName} (${participantId})`);
              publishStopScreenShare(
                { participantId },
                { persist: false }
              );
            }}
            title="Stop screen share"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-orange-400 hover:text-orange-300"
            >
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
              <line x1="2" y1="3" x2="22" y2="17" stroke="#f97316"></line>
            </svg>
          </div>
        )}

        {meetingRole === "moderator" && !isLocal && (
          <div
            className="m-1 p-1 cursor-pointer"
            onClick={() => remove(participantId)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-white"
            >
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </div>
        )}

        {meetingSettings.allowStageAccess > 0 && !isPresenter && isLocal && meetingRole !== "moderator" && (
          <div
            className="m-1 p-1 cursor-pointer"
            onClick={() => {
              console.log("WEBINAR_PARTICIPANT_REQUEST", participantId);
              publishParticipantRequest(
                {
                  participantId: participantId,
                },
                {
                  persist: false,
                }
              );
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 512"
              className="text-white w-5 h-5"
            >
              <path
                fill="white"
                d="M177 255.7l136 136c9.4 9.4 9.4 24.6 0 33.9l-22.6 22.6c-9.4 9.4-24.6 9.4-33.9 0L160 351.9l-96.4 96.4c-9.4 9.4-24.6 9.4-33.9 0L7 425.7c-9.4-9.4-9.4-24.6 0-33.9l136-136c9.4-9.5 24.6-9.5 34-.1zm-34-192L7 199.7c-9.4 9.4-9.4 24.6 0 33.9l22.6 22.6c9.4 9.4 24.6 9.4 33.9 0l96.4-96.4 96.4 96.4c9.4 9.4 24.6 9.4 33.9 0l22.6-22.6c9.4-9.4 9.4-24.6 0-33.9l-136-136c-9.2-9.4-24.4-9.4-33.8 0z"
              />
            </svg>
          </div>
        )}
        {!isPresenter &&
          (meetingRole == "moderator" || (meetingSettings.allowCoHostsManage && meetingRole === "co-host")) &&
          metaData?.meetingRole != "moderator" && (
            <div
              className="m-1 p-1 cursor-pointer"
              onClick={() => {
                console.log("WEBINAR_PARTICIPANT_REQUEST", participantId);
                publishParticipantModeChangeListener({
                  participantId: participantId,
                  mode: Constants.modes.SEND_AND_RECV,
                  action: "approve",
                });
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                className="text-white w-5 h-5"
              >
                <path
                  fill="white"
                  d="M177 255.7l136 136c9.4 9.4 9.4 24.6 0 33.9l-22.6 22.6c-9.4 9.4-24.6 9.4-33.9 0L160 351.9l-96.4 96.4c-9.4 9.4-24.6 9.4-33.9 0L7 425.7c-9.4-9.4-9.4-24.6 0-33.9l136-136c9.4-9.5 24.6-9.5 34-.1zm-34-192L7 199.7c-9.4 9.4-9.4 24.6 0 33.9l22.6 22.6c9.4 9.4 24.6 9.4 33.9 0l96.4-96.4 96.4 96.4c9.4 9.4 24.6 9.4 33.9 0l22.6-22.6c9.4-9.4 9.4-24.6 0-33.9l-136-136c-9.2-9.4-24.4-9.4-33.8 0z"
                />
              </svg>
            </div>
          )}

        {isPresenter && isLocal && meetingRole !== "moderator" && (
          <div
            className="m-1 p-1 cursor-pointer"
            onClick={() => {
              publishParticipantModeChangeListener({
                participantId: participantId,
                mode: Constants.modes.RECV_ONLY,
                action: "deny",
              });
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 512"
              className="text-white w-5 h-5"
            >
              <path
                fill="white"
                d="M143 256.3L7 120.3c-9.4-9.4-9.4-24.6 0-33.9l22.6-22.6c9.4-9.4 24.6-9.4 33.9 0l96.4 96.4 96.4-96.4c9.4-9.4 24.6-9.4 33.9 0L313 86.3c9.4 9.4 9.4 24.6 0 33.9l-136 136c-9.4 9.5-24.6 9.5-34 .1zm34 192l136-136c9.4-9.4 9.4-24.6 0-33.9l-22.6-22.6c-9.4-9.4-24.6-9.4-33.9 0L160 352.1l-96.4-96.4c-9.4-9.4-24.6-9.4-33.9 0L7 278.3c-9.4 9.4-9.4 24.6 0 33.9l136 136c9.4 9.5 24.6 9.5 34 .1z"
              />
            </svg>
          </div>
        )}

        {isPresenter &&
          (meetingRole == "moderator" || (meetingSettings.allowCoHostsManage && meetingRole === "co-host")) &&
          metaData?.meetingRole != "moderator" && (
            <div
              onClick={() => {
                publishParticipantModeChangeListener({
                  participantId: participantId,
                  mode: Constants.modes.RECV_ONLY,
                  action: "deny",
                });
              }}
              className="m-1 p-1 cursor-pointer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                className="text-white w-5 h-5"
              >
                <path
                  fill="white"
                  d="M143 256.3L7 120.3c-9.4-9.4-9.4-24.6 0-33.9l22.6-22.6c9.4-9.4 24.6-9.4 33.9 0l96.4 96.4 96.4-96.4c9.4-9.4 24.6-9.4 33.9 0L313 86.3c9.4 9.4 9.4 24.6 0 33.9l-136 136c-9.4 9.5-24.6 9.5-34 .1zm34 192l136-136c9.4-9.4 9.4-24.6 0-33.9l-22.6-22.6c-9.4-9.4-24.6-9.4-33.9 0L160 352.1l-96.4-96.4c-9.4-9.4-24.6-9.4-33.9 0L7 278.3c-9.4 9.4-9.4 24.6 0 33.9l136 136c9.4 9.5 24.6 9.5 34 .1z"
                />
              </svg>
            </div>
          )}
      </div>
    </div>
  );
}

export function WebinarParticipantPanel({ panelHeight }) {
  const { raisedHandsParticipants, participantApproveFunc, setParticipantApproveFunc, participantWebinarFunc, setParticipantWebinarFunc } =
    useMeetingAppContext();
  const mMeeting = useMeeting();
  const participants = mMeeting.participants;
  const presenterId = mMeeting?.presenterId;
  const { meetingRole, meetingSettings } = useSiteContext();
  const { publish } = usePubSub("MUTE_MIC");
  const { publish: publishParticipantModeChangeListener } = usePubSub("WEBINAR_PARTICIPANT_MODE_CHANGE_LISTENER");

  const sortedRaisedHandsParticipants = useMemo(() => {
    const participantIds = [...participants.keys()];

    const notRaised = participantIds.filter(
      (pID) =>
        raisedHandsParticipants.findIndex(
          ({ participantId: rPID }) => rPID === pID
        ) === -1
    );

    // Sort by raisedHandOn ascending (chronological order - first raised appears first)
    const raisedSorted = [...raisedHandsParticipants].sort((a, b) => {
      return a.raisedHandOn - b.raisedHandOn;
    });

    const combined = [
      ...raisedSorted.map(({ participantId: p }) => ({
        raisedHand: true,
        participantId: p,
      })),
      ...notRaised.map((p) => ({ raisedHand: false, participantId: p })),
    ];

    return combined;
  }, [raisedHandsParticipants, participants]);

  // Separate participants by mode
  const { presenters, viewers } = useMemo(() => {
    const presenters = [];
    const viewers = [];

    sortedRaisedHandsParticipants.forEach((item) => {
      const participant = participants.get(item.participantId);
      if (participant?.mode === Constants.modes.SEND_AND_RECV) {
        presenters.push(item);
      } else {
        viewers.push(item);
      }
    });

    return { presenters, viewers };
  }, [sortedRaisedHandsParticipants, participants]);

  const handleMuteAll = () => {
    // Get all participants
    const participantIds = [...participants.keys()];

    // Mute all participants except moderators
    participantIds.forEach((participantId) => {
      const participant = participants.get(participantId);
      // Only mute if participant has mic on and is not a moderator
      if (
        participant?.micOn &&
        participant?.mode === Constants.modes.SEND_AND_RECV &&
        participant?.metaData?.meetingRole !== "moderator" &&
        !participant?.isLocal
      ) {
        publish(
          {
            participantId: participantId,
          },
          {
            persist: false,
          }
        );
      }
    });
  };

  return (
    <div
      className={`flex w-full flex-col bg-gray-750 overflow-y-auto`}
      style={{ height: panelHeight }}
    >
      {/* {meetingRole === "moderator" && (
                <div className="flex items-center justify-between p-3 border-b border-gray-700">
                    <button
                        onClick={handleMuteAll}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                        Mute All Attendees
                    </button>
                </div>
            )} */}

      {/* On stage / watching count */}
      <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-700 flex items-center gap-2">
        <span className="text-green-400 font-medium">{presenters.length}</span> on stage
        <span className="mx-1 text-gray-600">·</span>
        <span className="text-blue-400 font-medium">{viewers.length}</span> watching
      </div>

      {/* Waiting Room Approval Section */}
      {participantApproveFunc.length > 0 && (
        <div
          className="flex flex-col border-b border-gray-700"
          style={{
            height: participantApproveFunc.length > 4 ? "40vh" : "auto",
            overflowY: "auto",
          }}
        >
          <div className="sticky top-0 bg-gray-800 px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
            Waiting Room ({participantApproveFunc.length})
          </div>
          {participantApproveFunc.map((item) => {
            return (
              <div
                key={item.participantId}
                className="flex items-start justify-between p-4 border-b border-gray-700/50"
              >
                <div className="text-white">{item.name}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      item.allow();
                      setParticipantApproveFunc(
                        participantApproveFunc.filter(
                          (pitem) => pitem.participantId !== item.participantId
                        )
                      );
                    }}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Admit
                  </button>
                  <button
                    onClick={() => {
                      item.deny();
                      setTimeout(() => {
                        setParticipantApproveFunc(
                          participantApproveFunc.filter(
                            (pitem) => pitem.participantId !== item.participantId
                          )
                        );
                      }, 500);
                    }}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Deny
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Webinar Upgrade Requests Section */}
      {(meetingRole == "moderator" || (meetingSettings.allowCoHostsManage && meetingRole === "co-host")) && participantWebinarFunc.length > 0 && (
        <div
          className="flex flex-col border-b border-gray-700"
          style={{
            height: participantWebinarFunc.length > 4 ? "40vh" : "auto",
            overflowY: "auto",
          }}
        >
          <div className="sticky top-0 bg-gray-800 px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
            Upgrade Requests ({participantWebinarFunc.length})
          </div>
          {participantWebinarFunc.map((item) => {
            return (
              <div
                key={item.participantId}
                className="flex items-start justify-between p-4 border-b border-gray-700/50"
              >
                <div className="text-white">{item.name}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setParticipantWebinarFunc(
                        participantWebinarFunc.filter(
                          (pitem) => pitem.participantId !== item.participantId
                        )
                      );
                      publishParticipantModeChangeListener(
                        {
                          participantId: item.participantId,
                          mode: Constants.modes.SEND_AND_RECV,
                          action: "approve",
                        });
                    }}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      publishParticipantModeChangeListener(
                        {
                          participantId: item.participantId,
                          mode: Constants.modes.RECV_ONLY,
                          action: "deny",
                        });
                      setTimeout(() => {
                        setParticipantWebinarFunc(
                          participantWebinarFunc.filter(
                            (pitem) => pitem.participantId !== item.participantId
                          )
                        );
                      }, 500);
                    }}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Deny
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Presenters Section */}
      {presenters.length > 0 && (
        <div className="flex flex-col">
          <div className="sticky top-0 bg-gray-800 px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
            Presenters ({presenters.length})
          </div>
          <div className="flex flex-col">
            {presenters.map(({ raisedHand, participantId }) => (
              <WebinarParticipantListItem
                key={participantId}
                participantId={participantId}
                raisedHand={raisedHand}
                isPresenting={presenterId === participantId}
              />
            ))}
          </div>
        </div>
      )}

      {/* Viewers Section */}
      {meetingSettings.showAttendeeList && viewers.length > 0 && (
        <div className="flex flex-col">
          <div className="sticky top-0 bg-gray-800 px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
            Attendees {meetingSettings.showAttendeeCount ? `(${viewers.length})` : ""}
          </div>
          <div className="flex flex-col">
            {viewers.map(({ raisedHand, participantId }) => (
              <WebinarParticipantListItem
                key={participantId}
                participantId={participantId}
                raisedHand={raisedHand}
                isPresenting={presenterId === participantId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

